/* istanbul ignore file */

import { useContext, useEffect } from 'react';

import { BULK_USER_TRACK_EVENTS, BulkUserCreationProps } from './BulkUserCreation.decl';
import { BulkUserCreationPrepareFile } from './BulkUserCreationPrepareFile/BulkUserCreationPrepareFile';
import {
  BulkUserCreationContext,
  BulkUserCreationProvider,
} from './BulkUserCreationProvider/BulkUserCreationProvider';
import { BulkUserCreationRecap } from './BulkUserCreationRecap/BulkUserCreationRecap';
import { BulkUserCreationSendInvitations } from './BulkUserCreationSendInvitations/BulkUserCreationSendInvitations';
import { BulkUserCreationUploadFile } from './BulkUserCreationUploadFile/BulkUserCreationUploadFile';

import { ThemeProvider } from '@aircall/tractor-v2';
import { FormWizard, capitalizeFirstLetter } from '@dashboard/library';
import { BulkUserError } from '@generated/globalTypes';
import { useTracker } from '@hooks/useTracker';
import { useTranslation } from 'react-i18next';

export interface BulkUserCreationData {
  file: File | null;
  response: null | {
    createdCount: number | null;
    totalCount: number | null;
    error: BulkUserError | null;
  };
}

export function BulkUserCreationFormWizard({ onClose }: BulkUserCreationProps) {
  const { t } = useTranslation();
  const { track } = useTracker();
  const context = useContext(BulkUserCreationContext);

  useEffect(() => {
    track({
      event: BULK_USER_TRACK_EVENTS.STARTED,
      payload: context.state,
    });

    // We only want this to run once, so we ignore the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormWizard<BulkUserCreationData>
      onSuccess={onClose}
      onClose={onClose}
      initialData={{ file: null, response: null }}
    >
      <FormWizard.Stepper />

      <FormWizard.Header>
        <FormWizard.HeaderTitle>{t('bulk_user_creation.title')}</FormWizard.HeaderTitle>
        <FormWizard.HeaderClose>{capitalizeFirstLetter(t('generic.close'))}</FormWizard.HeaderClose>
      </FormWizard.Header>

      <FormWizard.Pages>
        <FormWizard.Page stepTitle={t('bulk_user_creation.prepare_file.title')}>
          <BulkUserCreationPrepareFile />
        </FormWizard.Page>

        <FormWizard.Page stepTitle={t('bulk_user_creation.upload_users_info.title')}>
          <BulkUserCreationUploadFile />
        </FormWizard.Page>

        <FormWizard.Page stepTitle={t('bulk_user_creation.send_invitations.title')}>
          <BulkUserCreationSendInvitations />
        </FormWizard.Page>

        <FormWizard.Page stepTitle={t('bulk_user_creation.recap.title')}>
          <BulkUserCreationRecap />
        </FormWizard.Page>
      </FormWizard.Pages>
    </FormWizard>
  );
}

export function BulkUserCreation(props: BulkUserCreationProps) {
  return (
    <ThemeProvider>
      <BulkUserCreationProvider>
        <BulkUserCreationFormWizard {...props} />
      </BulkUserCreationProvider>
    </ThemeProvider>
  );
}
